import React from "react";
import { Search, FileText, Pencil, Eye, Download, Trash2, RotateCcw, Lock, Copy, AlertCircle, Camera } from "lucide-react";
import { Badge, btn, selectCls, fieldCls, Spinner, useAuthBlob, fmtBytes, fmtDate, fmtDateTime, fmtEUR } from "@/components/accounting/ui";

const FLAGS = [
  { id: "", label: "Alle Belege" }, { id: "missing", label: "Angaben fehlen" }, { id: "duplicates", label: "Duplikate" },
  { id: "supplement", label: "Nachlieferungen" }, { id: "trash", label: "Papierkorb" },
];
const UPLOADED = [{ id: "", label: "Upload: jederzeit" }, { id: "today", label: "Heute hochgeladen" }, { id: "7", label: "Letzte 7 Tage" }, { id: "30", label: "Letzte 30 Tage" }];

const Thumb = ({ d }) => {
  const { src, failed } = useAuthBlob(d.has_thumb ? `/admin/accounting/docs/${d.id}/file?variant=thumb` : null);
  if (src) return <img src={src} alt="" className="w-full h-full object-cover object-top" />;
  if (d.has_thumb && !failed) return <Spinner className="w-4 h-4" />;
  return <FileText size={18} strokeWidth={1.5} className="text-faint" />;
};

export const DocFilters = ({ filters, setFilters, vehicles, meta }) => {
  const set = (k) => (e) => setFilters((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));
  return (
    <div className="flex flex-col lg:flex-row gap-2 lg:items-center" data-testid="acc-filters">
      <div className="relative flex-1 min-w-0">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-faint" />
        <input value={filters.search} onChange={set("search")} placeholder="Suche: Lieferant, Rechnungsnr., Notiz, FIN …" className={`${fieldCls} pl-9`} data-testid="acc-search-input" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:flex gap-2">
        <select value={filters.category} onChange={set("category")} className={selectCls} data-testid="acc-filter-category" aria-label="Kategorie">
          <option value="">Alle Kategorien</option>
          {meta.categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
        </select>
        <select value={filters.flag} onChange={set("flag")} className={selectCls} data-testid="acc-filter-flag" aria-label="Status">
          {FLAGS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
        </select>
        <select value={filters.uploaded} onChange={set("uploaded")} className={selectCls} data-testid="acc-filter-uploaded" aria-label="Upload-Datum">
          {UPLOADED.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
        </select>
        <select value={filters.vehicle_id} onChange={set("vehicle_id")} className={`${selectCls} lg:max-w-[180px]`} data-testid="acc-filter-vehicle" aria-label="Fahrzeug">
          <option value="">Alle Fahrzeuge</option>
          {vehicles.map((v) => <option key={v.id} value={v.id}>{v.title}</option>)}
        </select>
      </div>
      <label className="inline-flex items-center gap-2 text-xs text-dim whitespace-nowrap px-1">
        <input type="checkbox" checked={filters.all_periods} onChange={set("all_periods")} className="accent-[rgb(var(--c-brand))]" data-testid="acc-filter-all-periods" />
        Alle Monate
      </label>
    </div>
  );
};

const RowBadges = ({ d, catLabel, showPeriod, periodLabel }) => (
  <>
    <Badge>{catLabel}</Badge>
    {showPeriod && <Badge>{periodLabel(d.period)}</Badge>}
    {d.locked && <Badge tone="green" testId={`acc-doc-locked-${d.id}`}><Lock size={9} /> Übermittelt</Badge>}
    {d.supplement && !d.locked && <Badge tone="amber" testId={`acc-doc-supplement-${d.id}`}>Nachlieferung</Badge>}
    {d.missing.length > 0 && <Badge tone="amber" title={`Fehlt: ${d.missing.join(", ")}`} testId={`acc-doc-missing-${d.id}`}><AlertCircle size={9} /> Angaben fehlen</Badge>}
    {d.duplicates.length > 0 && <Badge tone="red" title="Identische Datei bereits vorhanden" testId={`acc-doc-duplicate-${d.id}`}><Copy size={9} /> Duplikat</Badge>}
    {d.source === "camera" && <Badge><Camera size={9} /> Foto</Badge>}
  </>
);

export const DocRow = ({ d, meta, periodLabel, showPeriod, trash, confirmDelete, onOpen, onDownload, onDelete, onRestore }) => {
  const cat = meta.categories.find((c) => c.id === d.category);
  const type = cat.types.find((t) => t.id === d.doc_type);
  const iconBtn = `${btn} px-2.5 md:px-3.5`;
  return (
    <div data-testid={`acc-doc-${d.id}`} className="rounded-xl border border-line bg-surface p-3 md:p-4 flex items-center gap-3 md:gap-4">
      <button type="button" onClick={() => onOpen(d)} aria-label="Beleg öffnen" data-testid={`acc-doc-thumb-${d.id}`}
        className="w-14 h-[72px] md:w-16 md:h-20 rounded-md overflow-hidden bg-elevated border border-line shrink-0 flex items-center justify-center hover:border-silver transition-colors">
        <Thumb d={d} />
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <p className="font-semibold text-sm text-ink truncate max-w-full mr-1" data-testid={`acc-doc-title-${d.id}`}>{d.counterparty || d.original_filename}</p>
          <RowBadges d={d} catLabel={cat.label} showPeriod={showPeriod} periodLabel={periodLabel} />
        </div>
        <p className="text-xs text-dim mt-1 truncate">{type?.label || "Belegart offen"} · {fmtDate(d.doc_date)} · {fmtEUR(d.gross_amount)}{d.invoice_no ? ` · Nr. ${d.invoice_no}` : ""}</p>
        <p className="text-[11px] text-faint mt-0.5 truncate">
          {d.original_filename} · {fmtBytes(d.size)} · hochgeladen {fmtDateTime(d.uploaded_at)}{d.vehicle_title ? ` · ${d.vehicle_title}` : ""}
        </p>
      </div>
      <div className="flex flex-col sm:flex-row gap-1.5 shrink-0">
        {trash ? (
          <button type="button" onClick={() => onRestore(d)} className={iconBtn} data-testid={`acc-doc-restore-${d.id}`}><RotateCcw size={12} /><span className="hidden md:inline">Wiederherstellen</span></button>
        ) : (
          <>
            <button type="button" onClick={() => onOpen(d)} className={iconBtn} data-testid={`acc-doc-edit-${d.id}`} aria-label={d.locked ? "Ansehen" : "Bearbeiten"}>
              {d.locked ? <Eye size={12} /> : <Pencil size={12} />}<span className="hidden md:inline">{d.locked ? "Ansehen" : "Bearbeiten"}</span>
            </button>
            <button type="button" onClick={() => onDownload(d)} className={iconBtn} data-testid={`acc-doc-download-${d.id}`} aria-label="Herunterladen"><Download size={12} /><span className="hidden md:inline">Download</span></button>
            {!d.locked && (
              <button type="button" onClick={() => onDelete(d)} data-testid={confirmDelete ? "acc-doc-confirm-delete-btn" : `acc-doc-delete-${d.id}`} aria-label="Löschen"
                className={`btn-pill inline-flex items-center justify-center gap-1.5 px-2.5 md:px-3.5 py-2 text-xs font-semibold transition-colors ${confirmDelete ? "bg-brand text-brand-ink" : "border border-brand/40 text-red-700 hover:bg-brand/10"}`}>
                <Trash2 size={12} /><span className={confirmDelete ? "" : "hidden md:inline"}>{confirmDelete ? "Wirklich?" : "Löschen"}</span>
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};
